import { readErrorMessage, readFieldErrors } from '@Components/util/utils'
import { RootState } from '@AppState/redux-store'
import { PlayslipThemeServiceAPI } from '@API/data-services/playslip-theme-service'
import { ColorInput } from '@Components/games/components/color-input'
import { WithPermissions } from '@Components/common/with-permissions'
import { blankToNull, colorOrFallback, isValidHexColor } from '@Components/games/utils'
import {
  AuthScopes,
  PlayslipMessageCatalogEntry,
  PlayslipTheme,
  ResponseError,
} from '@Generated/back-office-client/src'
import {
  Button,
  Container,
  Flex,
  Heading,
  Section,
  Skeleton,
  Text,
  TextField,
} from '@radix-ui/themes'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link, useParams } from 'react-router-dom'
import {
  BUILT_IN_COLORS,
  DEFAULT_THEME_ID,
  HEX_COLOR_ERROR,
  SAVE_FAILED_MESSAGE,
  THEME_LOAD_FAILED_MESSAGE,
  THEME_SAVED_MESSAGE,
  COLOR_ROLES,
  placeholderError,
} from './constants'
import { ThemeMessages } from './playslip-theme-messages'
import {
  ThemeColorsFormData,
  ThemeColorErrors,
  ThemeColorRole,
  ThemeMessageErrors,
  ThemeMessagesFormData,
} from './types'
import {
  messageCatalogQueryKey,
  themeQueryKey,
  themesQueryKey,
  toColorsFormData,
  toColorsRequest,
  toMessagesFormData,
  unknownPlaceholders,
} from './utils'
import './styles.scss'

export const PlayslipThemeEditor = () => {
  const { activeCustomerId } = useSelector((state: RootState) => state.users)
  const { themeId = '' } = useParams()

  const {
    data: theme,
    isLoading,
    error: loadError,
  } = useQuery({
    queryKey: themeQueryKey(activeCustomerId, themeId),
    refetchOnMount: 'always',
    queryFn: () => PlayslipThemeServiceAPI.getPlayslipTheme(themeId),
    // A 4xx answers the same way every time, so retrying only delays the load error.
    retry: (failureCount, error) => {
      if (error instanceof ResponseError && error.response.status < 500) {
        return false
      }

      return failureCount < 1
    },
  })

  return (
    <Container>
      <Section>
        <Flex direction="column" gap="4">
          <Link to=".." relative="path">
            <Text size="2">← Back to playslip themes</Text>
          </Link>

          <Flex gap="2" align="center">
            <Heading size="6">{themeId}</Heading>
            {themeId === DEFAULT_THEME_ID && (
              <Text size="2" color="gray">
                Used by a host app that names no theme
              </Text>
            )}
          </Flex>

          {isLoading && (
            <Skeleton loading={true}>
              <Flex direction="column" gap="2" data-testid="playslip-theme-loading-skeleton">
                <div className="playslip-theme-skeleton-card" />
                <div className="playslip-theme-skeleton-card" />
              </Flex>
            </Skeleton>
          )}

          {loadError && (
            <Text size="2" color="red" data-testid="playslip-theme-load-error">
              {THEME_LOAD_FAILED_MESSAGE}
            </Text>
          )}

          {theme && <ThemeForm theme={theme} />}
        </Flex>
      </Section>
    </Container>
  )
}

/**
 * The colour, logo and copy fields for one theme. Split out so the form state starts from a theme that
 * has already loaded, rather than being seeded after the fact.
 */
const ThemeForm = ({ theme }: { theme: PlayslipTheme }) => {
  const { activeCustomerId } = useSelector((state: RootState) => state.users)
  const queryClient = useQueryClient()
  const [form, setForm] = useState<ThemeColorsFormData>(() => toColorsFormData(theme))
  const [partnerLogoUrl, setPartnerLogoUrl] = useState(theme.partnerLogoUrl ?? '')
  const [errors, setErrors] = useState<ThemeColorErrors>({})
  const [messages, setMessages] = useState<ThemeMessagesFormData>(() => toMessagesFormData(theme))
  const [messageErrors, setMessageErrors] = useState<ThemeMessageErrors>({})
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const {
    data: catalogResponse,
    isLoading: isCatalogLoading,
    error: catalogError,
  } = useQuery({
    queryKey: messageCatalogQueryKey(activeCustomerId),
    queryFn: () => PlayslipThemeServiceAPI.getPlayslipMessageCatalog(),
    // The catalog only changes with a deploy.
    staleTime: Infinity,
  })
  const catalog = catalogResponse?.items

  const saveMutation = useMutation({
    mutationFn: () =>
      PlayslipThemeServiceAPI.updatePlayslipTheme(theme.themeId, {
        colors: toColorsRequest(form),
        partnerLogoUrl: blankToNull(partnerLogoUrl),
        // Sent as typed: the service drops blanks and serves no key the catalog has retired.
        messages,
      }),
    onSuccess: () => {
      setSaveError(null)
      setErrors({})
      setSaved(true)
      queryClient.invalidateQueries({ queryKey: themeQueryKey(activeCustomerId, theme.themeId) })
      // The list draws a swatch row per theme, so it is stale the moment a colour changes.
      queryClient.invalidateQueries({ queryKey: themesQueryKey(activeCustomerId) })
    },
    onError: async (error: unknown) => {
      setSaved(false)
      // The endpoint validates the colours too, and names the role that failed.
      const fieldErrors = await readFieldErrors(error)
      const rejectedRoles = COLOR_ROLES.filter(({ role }) => fieldErrors[role])
      setErrors(Object.fromEntries(rejectedRoles.map(({ role }) => [role, fieldErrors[role]])))
      // A rejection the fields already carry needs no second copy above the save control.
      setSaveError(
        rejectedRoles.length > 0 ? null : await readErrorMessage(error, SAVE_FAILED_MESSAGE),
      )
    },
  })

  const validateColor = (role: ThemeColorRole) =>
    setErrors((current) => ({
      ...current,
      [role]: isValidHexColor(form[role]) ? undefined : HEX_COLOR_ERROR,
    }))

  const messageError = (entry: PlayslipMessageCatalogEntry): string | undefined => {
    const unknown = unknownPlaceholders(messages[entry.key] ?? '', entry.defaultText)

    return unknown.length > 0 ? placeholderError(unknown) : undefined
  }

  const validateMessage = (entry: PlayslipMessageCatalogEntry) =>
    setMessageErrors((current) => ({ ...current, [entry.key]: messageError(entry) }))

  const handleSave = () => {
    const invalid = COLOR_ROLES.filter(({ role }) => !isValidHexColor(form[role]))
    setErrors(Object.fromEntries(invalid.map(({ role }) => [role, HEX_COLOR_ERROR])))
    const invalidMessages = (catalog ?? []).filter((entry) => messageError(entry))
    setMessageErrors(
      Object.fromEntries(invalidMessages.map((entry) => [entry.key, messageError(entry)])),
    )
    if (invalid.length > 0 || invalidMessages.length > 0) {
      return
    }

    setSaveError(null)
    setSaved(false)
    saveMutation.mutate()
  }

  return (
    <Flex direction="column" gap="3">
      <Text size="2" color="gray">
        A colour left blank keeps the app&apos;s built-in colour for that role.
      </Text>

      {COLOR_ROLES.map(({ role, label, description }) => (
        <Flex key={role} direction="column" gap="1">
          <ColorInput
            id={`playslip-theme-${role}`}
            label={label}
            value={form[role]}
            error={errors[role]}
            onChange={(value) => {
              setForm({ ...form, [role]: value })
              setSaved(false)
            }}
            onBlur={() => validateColor(role)}
          />
          <Text size="1" color="gray">
            {description}
          </Text>
        </Flex>
      ))}

      <Flex direction="column" gap="1">
        <label htmlFor="playslip-theme-partner-logo-url">
          <Text size="2" weight="medium">
            Partner logo URL
          </Text>
        </label>
        <TextField.Root
          id="playslip-theme-partner-logo-url"
          value={partnerLogoUrl}
          onChange={(e) => {
            setPartnerLogoUrl(e.target.value)
            setSaved(false)
          }}
        />
        <Text size="1" color="gray">
          The retailer&apos;s logo, shown beside the lottery&apos;s in the playslip header.
        </Text>
      </Flex>

      <ThemePreview form={form} partnerLogoUrl={partnerLogoUrl} />

      <ThemeMessages
        catalog={catalog}
        isLoading={isCatalogLoading}
        loadFailed={Boolean(catalogError)}
        messages={messages}
        errors={messageErrors}
        onChange={(key, text) => {
          setMessages({ ...messages, [key]: text })
          setSaved(false)
        }}
        onBlur={validateMessage}
      />

      <WithPermissions requiredPermissions={AuthScopes.writegames}>
        <Flex direction="column" gap="2">
          <Flex>
            <Button onClick={handleSave} disabled={saveMutation.isPending}>
              {saveMutation.isPending ? 'Saving...' : 'Save theme'}
            </Button>
          </Flex>
          {saveError && (
            <Text size="1" color="red">
              {saveError}
            </Text>
          )}
          {saved && (
            <Text size="1" color="green">
              {THEME_SAVED_MESSAGE}
            </Text>
          )}
        </Flex>
      </WithPermissions>
    </Flex>
  )
}

type ThemePreviewProps = {
  form: ThemeColorsFormData
  partnerLogoUrl: string
}

/**
 * A miniature of the playslip the app draws, fed by the form rather than the saved theme so it
 * moves with every keystroke. A role left blank draws the built-in colour the app would use.
 */
const ThemePreview = ({ form, partnerLogoUrl }: ThemePreviewProps) => {
  const color = (role: ThemeColorRole) => colorOrFallback(form[role], BUILT_IN_COLORS[role])
  const logoUrl = partnerLogoUrl.trim()
  const [isLogoBroken, setIsLogoBroken] = useState(false)

  // A URL that failed is only broken until it is edited, so each new one gets a fresh attempt.
  useEffect(() => setIsLogoBroken(false), [logoUrl])

  return (
    <Flex direction="column" gap="2">
      <Text size="2" weight="medium">
        Preview
      </Text>
      <Flex
        data-testid="playslip-theme-preview"
        className="playslip-theme-preview"
        direction="column"
        gap="3"
        style={{ borderColor: color('borderColor') }}>
        {logoUrl && !isLogoBroken && (
          <img
            data-testid="playslip-theme-preview-partner-logo"
            className="playslip-theme-preview-logo"
            src={logoUrl}
            alt=""
            onError={() => setIsLogoBroken(true)}
          />
        )}
        <Text
          data-testid="playslip-theme-preview-font-color-primary"
          size="3"
          weight="bold"
          style={{ color: color('fontColorPrimary') }}>
          Pick 5 numbers
        </Text>
        <Text
          data-testid="playslip-theme-preview-font-color-secondary"
          size="1"
          style={{ color: color('fontColorSecondary') }}>
          3 of 5 chosen
        </Text>
        <Flex gap="2" align="center">
          <span
            data-testid="playslip-theme-preview-accentColor-color"
            className="playslip-theme-preview-number"
            style={{ backgroundColor: color('accentColor'), color: color('ctaFontColor') }}>
            7
          </span>
          <span
            data-testid="playslip-theme-preview-accentColor-color-background-color"
            className="playslip-theme-preview-number"
            style={{
              backgroundColor: color('accentBackgroundColor'),
              color: color('fontColorPrimary'),
            }}>
            12
          </span>
        </Flex>
        <div
          data-testid="playslip-theme-preview-border-color"
          className="playslip-theme-preview-rule"
          style={{ backgroundColor: color('borderColor') }}
        />
        <div
          data-testid="playslip-theme-preview-cta"
          className="playslip-theme-preview-cta"
          style={{ backgroundColor: color('ctaColor'), color: color('ctaFontColor') }}>
          Add to Order
        </div>
      </Flex>
    </Flex>
  )
}
